/**
 * BranchPolicySettingsPage - 지점 정책 설정 페이지
 *
 * /centers/:centerId/settings 경로로 접근하며,
 * 강사 수업료 위탁대금(instructorPayScale)과 시니어 강사 수업료 위탁대금(seniorInstructorPayScale)을
 * 나란히 편집하고, 연동된 강사 목록을 드래그앤드롭으로 시니어/일반 배정하며,
 * 수업 일지 형식(journalFormat)을 읽기 전용으로 표시한다.
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import {
  getBranchPolicy,
  saveBranchPolicy,
  type BranchPolicy,
} from "@/services/firestore/branch-policy";
import {
  getInstructors,
  updateInstructorSeniorStatus,
} from "@/services/firestore/admin-instructors";
import type { Instructor } from "@vlab-corporation/vlab-pilates-service-interface/staff";
import { normalizeClassType } from "@vlab-corporation/vlab-pilates-service-interface/branch";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** 드래그 가능한 강사 카드 */
function InstructorCard({
  instructor,
  onSeniorSinceChange,
}: {
  instructor: Instructor;
  onSeniorSinceChange?: (instructorId: string, date: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: instructor.id,
  });

  const [isEditingDate, setIsEditingDate] = useState(false);
  const [dateValue, setDateValue] = useState(instructor.seniorSince ?? "");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDateSave = () => {
    if (dateValue && onSeniorSinceChange) {
      onSeniorSinceChange(instructor.id, dateValue);
    }
    setIsEditingDate(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-gray-400 hover:text-gray-600"
        aria-label="드래그"
      >
        &#x2261;
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-800 truncate block">{instructor.name}</span>
        {instructor.seniorSince != null && (
          <div className="mt-0.5">
            {isEditingDate ? (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                  onBlur={handleDateSave}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleDateSave();
                    if (e.key === "Escape") setIsEditingDate(false);
                  }}
                  className="text-xs border border-blue-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            ) : (
              <button
                onClick={() => setIsEditingDate(true)}
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                title="클릭하여 시니어 적용일 수정"
              >
                시니어 적용: {instructor.seniorSince}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** 드래그 오버레이용 강사 카드 (미리보기) */
function DragOverlayCard({ instructor }: { instructor: Instructor }) {
  return (
    <div className="flex items-center gap-3 bg-white border-2 border-blue-400 rounded-md px-3 py-2 shadow-lg">
      <span className="text-gray-400">&#x2261;</span>
      <span className="text-sm font-medium text-gray-800">{instructor.name}</span>
    </div>
  );
}

/** 드롭 가능 영역 - useDroppable로 빈 영역에도 드롭 가능하게 함 */
function DroppableZone({
  id,
  children,
  className,
}: {
  id: string;
  children: React.ReactNode;
  className: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`${className}${isOver ? " ring-2 ring-blue-400" : ""}`}>
      {children}
    </div>
  );
}

/** 위탁대금 입력 필드 */
function PriceInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-4">
      <label htmlFor={id} className="text-sm text-gray-600 w-36 shrink-0">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        placeholder="금액 (원)"
      />
      <span className="text-sm text-gray-500">원</span>
    </div>
  );
}

export function BranchPolicySettingsPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const user = useCurrentUser();

  const [policy, setPolicy] = useState<BranchPolicy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 일반 강사 수업료 위탁대금 (1:1 정규)
  const [privateFirstPurchasePrice, setPrivateFirstPurchasePrice] = useState("");
  const [privateReRegistrationPrice, setPrivateReRegistrationPrice] = useState("");
  // 일반 강사 수업료 위탁대금 (1:1 OT)
  const [privateOtFirstPurchasePrice, setPrivateOtFirstPurchasePrice] = useState("");
  // 일반 강사 수업료 위탁대금 (그룹 정규 — 수업당 정산, 신규/재등록 동일)
  const [groupRegularPrice, setGroupRegularPrice] = useState("");
  // 일반 강사 수업료 위탁대금 (그룹 OT)
  const [groupOtFirstPurchasePrice, setGroupOtFirstPurchasePrice] = useState("");

  // 시니어 강사 수업료 위탁대금 (1:1 정규)
  const [seniorPrivateFirstPurchasePrice, setSeniorPrivateFirstPurchasePrice] = useState("");
  const [seniorPrivateReRegistrationPrice, setSeniorPrivateReRegistrationPrice] = useState("");
  // 시니어 강사 수업료 위탁대금 (1:1 OT)
  const [seniorPrivateOtFirstPurchasePrice, setSeniorPrivateOtFirstPurchasePrice] = useState("");
  // 시니어 강사 수업료 위탁대금 (그룹 정규 — 수업당 정산, 신규/재등록 동일)
  const [seniorGroupRegularPrice, setSeniorGroupRegularPrice] = useState("");
  // 시니어 강사 수업료 위탁대금 (그룹 OT)
  const [seniorGroupOtFirstPurchasePrice, setSeniorGroupOtFirstPurchasePrice] = useState("");

  // 노쇼 정산 금액
  const [noShowDeductionAmount, setNoShowDeductionAmount] = useState("20000");

  // 저장 상태
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // 강사 목록
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [instructorsLoading, setInstructorsLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  // 시니어 지정 모달
  const [seniorModal, setSeniorModal] = useState<{
    instructorId: string;
    instructorName: string;
  } | null>(null);
  const [seniorSinceInput, setSeniorSinceInput] = useState(new Date().toISOString().slice(0, 10));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const regularInstructors = instructors.filter((i) => i.seniorSince == null);
  const seniorInstructors = instructors.filter((i) => i.seniorSince != null);

  // 데이터 로드
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setIsLoading(true);
      setError(null);

      try {
        const [data, instructorList] = await Promise.all([
          getBranchPolicy(user.uid, Number(centerId)),
          getInstructors(user.uid, Number(centerId)),
        ]);
        if (cancelled) return;

        setPolicy(data);
        setInstructors(instructorList);

        // 일반 위탁대금 추출 (classType + isOt 기반) — @ClassType-values
        const findPrice = (
          scales: typeof data.instructorPayScale,
          mt: string,
          ct: string,
          isOt: boolean,
        ) =>
          scales.find(
            (s) =>
              s.memberType === mt &&
              normalizeClassType(s.classType ?? s.classFormat) === ct &&
              (s.isOt ?? false) === isOt,
          );

        const pfp = findPrice(data.instructorPayScale, "firstPurchase", "PRIVATE_REGULAR", false);
        const prr = findPrice(data.instructorPayScale, "reRegistration", "PRIVATE_REGULAR", false);
        const potfp = findPrice(data.instructorPayScale, "firstPurchase", "PRIVATE_REGULAR", true);
        const gfp = findPrice(data.instructorPayScale, "firstPurchase", "GROUP_REGULAR", false);
        const gotfp = findPrice(data.instructorPayScale, "firstPurchase", "GROUP_REGULAR", true);
        setPrivateFirstPurchasePrice(pfp ? String(pfp.unitPrice) : "");
        setPrivateReRegistrationPrice(prr ? String(prr.unitPrice) : "");
        setPrivateOtFirstPurchasePrice(potfp ? String(potfp.unitPrice) : "");
        setGroupRegularPrice(gfp ? String(gfp.unitPrice) : "");
        setGroupOtFirstPurchasePrice(gotfp ? String(gotfp.unitPrice) : "");

        // 시니어 위탁대금 추출
        const senior = data.seniorInstructorPayScale ?? [];
        const spfp = findPrice(senior, "firstPurchase", "PRIVATE_REGULAR", false);
        const sprr = findPrice(senior, "reRegistration", "PRIVATE_REGULAR", false);
        const spotfp = findPrice(senior, "firstPurchase", "PRIVATE_REGULAR", true);
        const sgfp = findPrice(senior, "firstPurchase", "GROUP_REGULAR", false);
        const sgotfp = findPrice(senior, "firstPurchase", "GROUP_REGULAR", true);
        setSeniorPrivateFirstPurchasePrice(spfp ? String(spfp.unitPrice) : "");
        setSeniorPrivateReRegistrationPrice(sprr ? String(sprr.unitPrice) : "");
        setSeniorPrivateOtFirstPurchasePrice(spotfp ? String(spotfp.unitPrice) : "");
        setSeniorGroupRegularPrice(sgfp ? String(sgfp.unitPrice) : "");
        setSeniorGroupOtFirstPurchasePrice(sgotfp ? String(sgotfp.unitPrice) : "");

        setNoShowDeductionAmount(
          data.noShowDeductionAmount != null ? String(data.noShowDeductionAmount) : "20000",
        );
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("지점 정책 조회 실패"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setInstructorsLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [user.uid, centerId]);

  const validateNonNegativeInt = (value: string, label: string): number | null => {
    const num = Number(value);
    if (value === "" || !Number.isInteger(num) || num < 0) {
      setValidationError(`${label}은(는) 0 이상의 정수여야 합니다.`);
      return null;
    }
    return num;
  };

  const handleSave = async () => {
    if (!policy) return;

    // 1:1 정규 위탁대금 검증
    const pfpNum = validateNonNegativeInt(privateFirstPurchasePrice, "1:1 신규 수업료");
    if (pfpNum === null) return;
    const prrNum = validateNonNegativeInt(privateReRegistrationPrice, "1:1 재등록 수업료");
    if (prrNum === null) return;
    // 1:1 OT 위탁대금 검증 (비어있으면 0 기본값)
    const potfpNum = validateNonNegativeInt(privateOtFirstPurchasePrice || "0", "1:1 OT 수업료");
    if (potfpNum === null) return;
    // 그룹 정규 위탁대금 검증 (비어있으면 0 기본값, 수업당 정산 단일 위탁대금)
    const groupRegularNum = validateNonNegativeInt(groupRegularPrice || "0", "그룹 수업료");
    if (groupRegularNum === null) return;
    // 그룹 OT 위탁대금 검증 (비어있으면 0 기본값)
    const gotfpNum = validateNonNegativeInt(groupOtFirstPurchasePrice || "0", "그룹 OT 수업료");
    if (gotfpNum === null) return;
    const nsNum = validateNonNegativeInt(noShowDeductionAmount, "노쇼 수업 정산 금액");
    if (nsNum === null) return;

    // 시니어 위탁대금: 비어있으면 설정하지 않음
    let seniorPayScale: BranchPolicy["seniorInstructorPayScale"];
    const hasSeniorPrices =
      seniorPrivateFirstPurchasePrice !== "" ||
      seniorPrivateReRegistrationPrice !== "" ||
      seniorPrivateOtFirstPurchasePrice !== "" ||
      seniorGroupRegularPrice !== "" ||
      seniorGroupOtFirstPurchasePrice !== "";
    if (hasSeniorPrices) {
      const spfpNum = validateNonNegativeInt(
        seniorPrivateFirstPurchasePrice || "0",
        "시니어 1:1 신규 수업료",
      );
      if (spfpNum === null) return;
      const sprrNum = validateNonNegativeInt(
        seniorPrivateReRegistrationPrice || "0",
        "시니어 1:1 재등록 수업료",
      );
      if (sprrNum === null) return;
      const spotfpNum = validateNonNegativeInt(
        seniorPrivateOtFirstPurchasePrice || "0",
        "시니어 1:1 OT 수업료",
      );
      if (spotfpNum === null) return;
      const seniorGroupRegularNum = validateNonNegativeInt(
        seniorGroupRegularPrice || "0",
        "시니어 그룹 수업료",
      );
      if (seniorGroupRegularNum === null) return;
      const sgotfpNum = validateNonNegativeInt(
        seniorGroupOtFirstPurchasePrice || "0",
        "시니어 그룹 OT 수업료",
      );
      if (sgotfpNum === null) return;
      seniorPayScale = [
        {
          memberType: "firstPurchase",
          classType: "PRIVATE_REGULAR",
          isOt: false,
          unitPrice: spfpNum,
        },
        {
          memberType: "reRegistration",
          classType: "PRIVATE_REGULAR",
          isOt: false,
          unitPrice: sprrNum,
        },
        {
          memberType: "firstPurchase",
          classType: "PRIVATE_REGULAR",
          isOt: true,
          unitPrice: spotfpNum,
        },
        {
          memberType: "firstPurchase",
          classType: "GROUP_REGULAR",
          isOt: false,
          unitPrice: seniorGroupRegularNum,
        },
        {
          memberType: "reRegistration",
          classType: "GROUP_REGULAR",
          isOt: false,
          unitPrice: seniorGroupRegularNum,
        },
        {
          memberType: "firstPurchase",
          classType: "GROUP_REGULAR",
          isOt: true,
          unitPrice: sgotfpNum,
        },
      ];
    }

    setValidationError(null);
    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      const updatedPolicy: BranchPolicy = {
        ...policy,
        instructorPayScale: [
          {
            memberType: "firstPurchase",
            classType: "PRIVATE_REGULAR",
            isOt: false,
            unitPrice: pfpNum,
          },
          {
            memberType: "reRegistration",
            classType: "PRIVATE_REGULAR",
            isOt: false,
            unitPrice: prrNum,
          },
          {
            memberType: "firstPurchase",
            classType: "PRIVATE_REGULAR",
            isOt: true,
            unitPrice: potfpNum,
          },
          {
            memberType: "firstPurchase",
            classType: "GROUP_REGULAR",
            isOt: false,
            unitPrice: groupRegularNum,
          },
          {
            memberType: "reRegistration",
            classType: "GROUP_REGULAR",
            isOt: false,
            unitPrice: groupRegularNum,
          },
          {
            memberType: "firstPurchase",
            classType: "GROUP_REGULAR",
            isOt: true,
            unitPrice: gotfpNum,
          },
        ],
        seniorInstructorPayScale: seniorPayScale,
        noShowDeductionAmount: nsNum,
      };
      await saveBranchPolicy(user.uid, Number(centerId), updatedPolicy);
      setPolicy(updatedPolicy);
      setSaveSuccess(true);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over) return;

      const draggedId = String(active.id);
      const instructor = instructors.find((i) => i.id === draggedId);
      if (!instructor) return;

      const overId = String(over.id);

      // 드롭 대상 영역 판별
      const isDropOnSeniorZone =
        overId === "senior-drop-zone" || seniorInstructors.some((i) => i.id === overId);
      const isDropOnRegularZone =
        overId === "regular-drop-zone" || regularInstructors.some((i) => i.id === overId);

      if (isDropOnSeniorZone && instructor.seniorSince == null) {
        // 일반 → 시니어: 모달로 seniorSince 입력
        setSeniorSinceInput(new Date().toISOString().slice(0, 10));
        setSeniorModal({ instructorId: draggedId, instructorName: instructor.name });
      } else if (isDropOnRegularZone && instructor.seniorSince != null) {
        // 시니어 → 일반: 즉시 해제
        void handleRemoveSenior(draggedId);
      }
    },
    [instructors, seniorInstructors, regularInstructors],
  );

  const handleConfirmSenior = async () => {
    if (!seniorModal || !seniorSinceInput) return;

    try {
      await updateInstructorSeniorStatus(
        user.uid,
        Number(centerId),
        seniorModal.instructorId,
        seniorSinceInput,
      );
      setInstructors((prev) =>
        prev.map((i) =>
          i.id === seniorModal.instructorId ? { ...i, seniorSince: seniorSinceInput } : i,
        ),
      );
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "시니어 지정에 실패했습니다.");
    }
    setSeniorModal(null);
  };

  const handleRemoveSenior = async (instructorId: string) => {
    try {
      await updateInstructorSeniorStatus(user.uid, Number(centerId), instructorId, null);
      setInstructors((prev) =>
        prev.map((i) => (i.id === instructorId ? { ...i, seniorSince: undefined } : i)),
      );
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "시니어 해제에 실패했습니다.");
    }
  };

  const handleSeniorSinceChange = async (instructorId: string, date: string) => {
    try {
      await updateInstructorSeniorStatus(user.uid, Number(centerId), instructorId, date);
      setInstructors((prev) =>
        prev.map((i) => (i.id === instructorId ? { ...i, seniorSince: date } : i)),
      );
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "적용일 변경에 실패했습니다.");
    }
  };

  const activeInstructor = activeId ? (instructors.find((i) => i.id === activeId) ?? null) : null;

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center">로딩 중...</p>
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  // --- 정상 상태 ---
  return (
    <div className="space-y-6">
      {/* 뒤로가기 링크 */}
      <div>
        <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 센터 상세
        </Link>
      </div>

      {/* 수업료 위탁대금 카드 - 좌우 나란히 배치 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">지점 정책 설정</h1>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* 일반 강사 수업료 위탁대금 */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800">강사 수업료 위탁대금</h2>
            <p className="text-xs text-gray-500 mt-1">일반 강사에게 적용되는 수업료 위탁대금</p>
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500">
                    <th className="text-left pb-2 font-medium">수업 유형</th>
                    <th className="text-center pb-2 font-medium">신규</th>
                    <th className="text-center pb-2 font-medium">재등록</th>
                  </tr>
                </thead>
                <tbody className="space-y-2">
                  <tr>
                    <td className="py-1.5 text-gray-700">1:1 정규</td>
                    <td className="py-1.5 px-2">
                      <input
                        id="privateFirstPurchasePrice"
                        type="number"
                        min={0}
                        step={1}
                        value={privateFirstPurchasePrice}
                        onChange={(e) => setPrivateFirstPurchasePrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        id="privateReRegistrationPrice"
                        type="number"
                        min={0}
                        step={1}
                        value={privateReRegistrationPrice}
                        onChange={(e) => setPrivateReRegistrationPrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-700">1:1 OT</td>
                    <td className="py-1.5 px-2">
                      <input
                        id="privateOtFirstPurchasePrice"
                        type="number"
                        min={0}
                        step={1}
                        value={privateOtFirstPurchasePrice}
                        onChange={(e) => setPrivateOtFirstPurchasePrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        type="number"
                        disabled
                        value=""
                        className="w-full px-2 py-1 text-sm border border-gray-200 rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                        placeholder="해당없음"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-700">
                      그룹 정규
                      <span className="block text-xs text-gray-400">수업당 정산</span>
                    </td>
                    <td className="py-1.5 px-2" colSpan={2}>
                      <input
                        id="groupRegularPrice"
                        type="number"
                        min={0}
                        step={1}
                        value={groupRegularPrice}
                        onChange={(e) => setGroupRegularPrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-700">그룹 OT</td>
                    <td className="py-1.5 px-2">
                      <input
                        id="groupOtFirstPurchasePrice"
                        type="number"
                        min={0}
                        step={1}
                        value={groupOtFirstPurchasePrice}
                        onChange={(e) => setGroupOtFirstPurchasePrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        type="number"
                        disabled
                        value=""
                        className="w-full px-2 py-1 text-sm border border-gray-200 rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                        placeholder="해당없음"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 시니어 강사 수업료 위탁대금 */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800">시니어 강사 수업료 위탁대금</h2>
            <p className="text-xs text-gray-500 mt-1">
              시니어 강사에게 적용되는 수업료 위탁대금 (미설정 시 일반 위탁대금 적용)
            </p>
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500">
                    <th className="text-left pb-2 font-medium">수업 유형</th>
                    <th className="text-center pb-2 font-medium">신규</th>
                    <th className="text-center pb-2 font-medium">재등록</th>
                  </tr>
                </thead>
                <tbody className="space-y-2">
                  <tr>
                    <td className="py-1.5 text-gray-700">1:1 정규</td>
                    <td className="py-1.5 px-2">
                      <input
                        id="seniorPrivateFirstPurchasePrice"
                        type="number"
                        min={0}
                        step={1}
                        value={seniorPrivateFirstPurchasePrice}
                        onChange={(e) => setSeniorPrivateFirstPurchasePrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        id="seniorPrivateReRegistrationPrice"
                        type="number"
                        min={0}
                        step={1}
                        value={seniorPrivateReRegistrationPrice}
                        onChange={(e) => setSeniorPrivateReRegistrationPrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-700">1:1 OT</td>
                    <td className="py-1.5 px-2">
                      <input
                        id="seniorPrivateOtFirstPurchasePrice"
                        type="number"
                        min={0}
                        step={1}
                        value={seniorPrivateOtFirstPurchasePrice}
                        onChange={(e) => setSeniorPrivateOtFirstPurchasePrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        type="number"
                        disabled
                        value=""
                        className="w-full px-2 py-1 text-sm border border-gray-200 rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                        placeholder="해당없음"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-700">
                      그룹 정규
                      <span className="block text-xs text-gray-400">수업당 정산</span>
                    </td>
                    <td className="py-1.5 px-2" colSpan={2}>
                      <input
                        id="seniorGroupRegularPrice"
                        type="number"
                        min={0}
                        step={1}
                        value={seniorGroupRegularPrice}
                        onChange={(e) => setSeniorGroupRegularPrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-700">그룹 OT</td>
                    <td className="py-1.5 px-2">
                      <input
                        id="seniorGroupOtFirstPurchasePrice"
                        type="number"
                        min={0}
                        step={1}
                        value={seniorGroupOtFirstPurchasePrice}
                        onChange={(e) => setSeniorGroupOtFirstPurchasePrice(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="원"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        type="number"
                        disabled
                        value=""
                        className="w-full px-2 py-1 text-sm border border-gray-200 rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                        placeholder="해당없음"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 노쇼 수업 정산 금액 */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-800">노쇼 수업 정산 금액</h2>
          <p className="text-sm text-gray-500 mt-1">
            전원 노쇼 처리된 수업의 강사 정산 금액입니다.
          </p>
          <div className="mt-4">
            <PriceInput
              id="noShowDeductionAmount"
              label="노쇼 정산 금액"
              value={noShowDeductionAmount}
              onChange={setNoShowDeductionAmount}
            />
          </div>
        </div>

        {/* 유효성/저장 메시지 */}
        {validationError && <p className="mt-3 text-sm text-red-600">{validationError}</p>}
        {saveError && <p className="mt-3 text-sm text-red-600">{saveError}</p>}
        {saveSuccess && <p className="mt-3 text-sm text-green-600">저장되었습니다.</p>}

        <button
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "저장 중..." : "저장"}
        </button>
      </div>

      {/* 강사 요금제 배정 카드 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800">강사 요금제 배정</h2>
        <p className="text-sm text-gray-500 mt-1">
          강사를 드래그하여 일반/시니어 요금제를 배정할 수 있습니다.
        </p>

        {instructorsLoading ? (
          <p className="mt-4 text-gray-500 text-sm text-center">강사 목록 로딩 중...</p>
        ) : instructors.length === 0 ? (
          <p className="mt-4 text-gray-500 text-sm text-center">연동된 강사가 없습니다.</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 일반 강사 */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  일반 강사 <span className="text-gray-400">({regularInstructors.length}명)</span>
                </h3>
                <SortableContext
                  id="regular-drop-zone"
                  items={regularInstructors.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableZone
                    id="regular-drop-zone"
                    className="min-h-[80px] space-y-2 p-3 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg"
                  >
                    {regularInstructors.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">
                        여기에 드래그하여 일반 강사로 변경
                      </p>
                    ) : (
                      regularInstructors.map((instructor) => (
                        <InstructorCard key={instructor.id} instructor={instructor} />
                      ))
                    )}
                  </DroppableZone>
                </SortableContext>
              </div>

              {/* 시니어 강사 */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  시니어 강사 <span className="text-gray-400">({seniorInstructors.length}명)</span>
                </h3>
                <SortableContext
                  id="senior-drop-zone"
                  items={seniorInstructors.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableZone
                    id="senior-drop-zone"
                    className="min-h-[80px] space-y-2 p-3 bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg"
                  >
                    {seniorInstructors.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">
                        여기에 드래그하여 시니어 강사로 변경
                      </p>
                    ) : (
                      seniorInstructors.map((instructor) => (
                        <InstructorCard
                          key={instructor.id}
                          instructor={instructor}
                          onSeniorSinceChange={handleSeniorSinceChange}
                        />
                      ))
                    )}
                  </DroppableZone>
                </SortableContext>
              </div>
            </div>

            <DragOverlay>
              {activeInstructor ? <DragOverlayCard instructor={activeInstructor} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* 수업 일지 형식 카드 (읽기 전용) */}
      {policy && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800">수업 일지 형식</h2>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600">
              활성화 여부:{" "}
              <span className="font-medium text-gray-900">
                {policy.journalFormat.enabled ? "활성화" : "비활성화"}
              </span>
            </p>
            {policy.journalFormat.templateName && (
              <p className="text-sm text-gray-600">
                템플릿명:{" "}
                <span className="font-medium text-gray-900">
                  {policy.journalFormat.templateName}
                </span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* 시니어 지정 모달 */}
      {seniorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900">시니어 강사 지정</h3>
            <p className="mt-2 text-sm text-gray-600">
              <span className="font-medium">{seniorModal.instructorName}</span> 강사를 시니어로
              지정합니다.
            </p>
            <div className="mt-4">
              <label htmlFor="seniorSinceDate" className="block text-sm font-medium text-gray-700">
                시니어 적용 시작일
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                이 날짜 이후 수업부터 시니어 위탁대금이 적용됩니다.
              </p>
              <input
                id="seniorSinceDate"
                type="date"
                value={seniorSinceInput}
                onChange={(e) => setSeniorSinceInput(e.target.value)}
                className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setSeniorModal(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => void handleConfirmSenior()}
                disabled={!seniorSinceInput}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
